import { useState, type FormEvent } from "react";
import { useReveal } from "@/hooks/useReveal";

const FORM_URL = "https://readdy.ai/api/form/da8v6dptacug2b80sm80";

export default function TerritoryForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const headingRef = useReveal<HTMLDivElement>();
  const formRef = useReveal<HTMLDivElement>();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = (formData.get("company_alt") as string) || "";
    if (hp.trim() !== "") {
      setStatus("success");
      setErrorMsg("");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt" && typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      const ok =
        res.ok && (parsed?.code === "OK" || (!parsed && responseText.length > 0));

      if (ok) {
        setStatus("success");
        setErrorMsg("");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "Something went wrong. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="quote" className="bg-ink py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div ref={headingRef} className="reveal text-center">
          <h2 className="text-cream font-serif text-3xl md:text-4xl font-semibold">
            Check territory availability
          </h2>
          <p className="mt-3 text-cream/70 text-lg">
            Five fields.
          </p>
        </div>

        <div
          ref={formRef}
          className="reveal mt-10 max-w-[640px] mx-auto bg-white rounded-lg p-7 md:p-10"
        >
          <form data-readdy-form onSubmit={handleSubmit} noValidate>
            <input
              type="text"
              name="company_alt"
              className="hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-ink text-sm font-semibold mb-1.5">
                  Work email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@company.co.za"
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-ink text-sm font-semibold mb-1.5">
                  Company name
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  placeholder="Your company"
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <label htmlFor="products" className="block text-ink text-sm font-semibold mb-1.5">
                  Which products interest you?
                </label>
                <select
                  id="products"
                  name="products"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option>Organic whole cane sugar</option>
                  <option>Organic cocoa powder</option>
                  <option>Both</option>
                  <option>The full range including coffee and infusions</option>
                  <option>Our own brand instead</option>
                </select>
              </div>

              <div>
                <label htmlFor="channels" className="block text-ink text-sm font-semibold mb-1.5">
                  Which channels do you supply?
                </label>
                <select
                  id="channels"
                  name="channels"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select a channel
                  </option>
                  <option>Health-food and organic stores</option>
                  <option>Pharmacy and wellness retail</option>
                  <option>Supermarket chains</option>
                  <option>Online retail</option>
                  <option>Foodservice and wholesale</option>
                  <option>Several of these</option>
                </select>
              </div>

              <div>
                <label htmlFor="territory" className="block text-ink text-sm font-semibold mb-1.5">
                  Territory you would want
                </label>
                <input
                  id="territory"
                  name="territory"
                  type="text"
                  placeholder="e.g. Western Cape, or all of South Africa"
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>

              <div>
                <label htmlFor="first_order" className="block text-ink text-sm font-semibold mb-1.5">
                  Estimated first order
                </label>
                <select
                  id="first_order"
                  name="first_order"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select an estimate
                  </option>
                  <option>Samples first</option>
                  <option>Under half a container</option>
                  <option>Half a container</option>
                  <option>One 40 ft container</option>
                  <option>More than one container</option>
                </select>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-ink text-sm font-semibold mb-1.5">
                  Target timeline
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select a timeline
                  </option>
                  <option>Within 1 month</option>
                  <option>1 to 3 months</option>
                  <option>3 to 6 months</option>
                  <option>Exploring only</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="mt-7 w-full inline-flex items-center justify-center px-6 py-4 rounded-md bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors"
            >
              Send request
            </button>

            {status === "success" && (
              <p className="mt-4 text-center text-emerald-700 text-sm font-medium">
                Thank you. We have received your request and will reply.
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 text-center text-red-600 text-sm font-medium">
                {errorMsg}
              </p>
            )}

            <p className="mt-5 text-center text-ink/50 text-xs leading-relaxed">
              We will only use these details to reply to your enquiry. You can
              ask us to delete your data at any time by writing to{" "}
              sara.grisales@mesabaja.com.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}